import { Link } from 'react-router';
import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideSteps,
  GuideStep,
  GuideGrid,
  GuideCard
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function DocumentingVehicleDamageGuide() {
  return (
    <GuideLayout
      slug="documenting-vehicle-damage"
      category="Evidence"
      categoryColor="accent"
      title="Documenting Your Vehicle Damage"
      description="What photos you need of your vehicle damage and how to clearly link it to the pothole incident."
      readTime="5 min"
      updatedDate="2026-01-12"
      datePublished="2026-01-10"
      reviewedFor="UK law"
      relatedGuides={[
        { slug: 'photographing-pothole-damage', title: 'How to Photograph Pothole Damage', readTime: '6 min' },
        { slug: 'getting-repair-quotes', title: 'Getting the Right Repair Quote', readTime: '4 min' },
        { slug: 'what-makes-a-valid-claim', title: 'What Makes a Valid Pothole Claim?', readTime: '5 min' },
      ]}
      atAGlance={[
        { label: 'Common damage types', value: 'Tyres, wheels, suspension, and tracking/alignment' },
        { label: 'When to photograph', value: 'Immediately, and always before any repairs are done' },
        { label: 'Photos per damaged area', value: 'At least 3-5 from different angles, plus overview shots' },
        { label: 'Proving it\'s your vehicle', value: 'Include the registration plate and show which wheel or corner was hit' },
        { label: 'Hidden damage', value: 'Get a garage inspection report linking the damage to the pothole impact' },
      ]}
      howTo={{
        name: 'How to Document Vehicle Damage for a Pothole Claim',
        description:
          'Four steps to photograph vehicle damage so it clearly links back to the pothole incident.',
        steps: [
          {
            name: 'Photograph immediately',
            text: "Take photos at the scene or as soon as the damage is noticed, in good daylight if possible. Don't clean the damage first — dirt and debris can show the impact.",
          },
          {
            name: 'Take multiple angles',
            text: 'Capture a wide shot of the whole wheel or tyre area, a medium shot focusing on the damage, a close-up of the specific damage, and an angle showing depth for dents, cuts, or bulges.',
          },
          {
            name: 'Include context',
            text: 'Include the registration plate in at least one shot, show which wheel or corner of the vehicle was affected, and photograph near the pothole location if possible.',
          },
          {
            name: 'Use a reference object',
            text: 'For damage that is hard to gauge visually, include something for scale, such as a coin next to a tyre bulge or cut, a ruler, or a hand for rough scale.',
          },
        ],
      }}
      faqs={[
        {
          question: 'What if my damage isn\'t visible in photos?',
          answer: 'Some damage like internal tyre damage or alignment issues won\'t show in photos. In these cases, the garage\'s professional assessment becomes your key evidence. Ask them to photograph any internal damage they find and include detailed descriptions in their report.',
        },
        {
          question: 'Should I photograph the damage before or after repair?',
          answer: 'Always photograph before repair if possible. Once fixed, the evidence is gone. If you\'ve already had repairs done, provide the garage\'s photos and documentation instead.',
        },
        {
          question: 'Can I claim for damage I can\'t see yet?',
          answer: 'Yes, but you\'ll need a professional inspection. Hidden damage like tracking misalignment or internal suspension issues can be documented by a garage. Get an inspection report that links the damage to the pothole impact.',
        },
        {
          question: 'What if there was pre-existing damage?',
          answer: 'Councils only pay for damage caused by the specific incident. If there was pre-existing wear or damage, be honest about it. A garage can often assess what damage is fresh versus pre-existing.',
        },
        {
          question: 'Do I need photos from multiple angles?',
          answer: 'Yes, multiple angles help prove the damage is real and show its extent. Take at least 3-5 photos of each damaged area from different angles, plus overview shots showing the damaged area in context.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
        title="Why Vehicle Damage Photos Matter"
      >
        <p className="mb-4">
          Photographs of your vehicle damage are crucial evidence in a{' '}
          <Link to="/guides/what-makes-a-valid-claim" className={inlineLink}>
            valid pothole claim
          </Link>
          . They prove that real damage occurred 
          and help establish the connection between the pothole and the harm to your vehicle. Poor or 
          missing photos are a{' '}
          <Link to="/guides/understanding-rejection-reasons" className={inlineLink}>
            common reason claims fail
          </Link>
          .
        </p>
        <GuideTip variant="info" title="Key principle">
          Your photos should tell a clear story: "This damage happened to my vehicle because of that pothole."
        </GuideTip>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        }
        title="Common Types of Damage"
      >
        <GuideGrid cols={2}>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Tyre Damage"
          >
            Sidewall bulges, cuts, punctures, or blowouts. Often the most common and easiest to photograph.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Wheel Damage"
          >
            Buckled, cracked, or scuffed alloy wheels. Photograph the rim from multiple angles.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            }
            title="Suspension Damage"
          >
            Springs, shock absorbers, control arms. Often requires professional inspection to identify.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            }
            title="Tracking/Alignment"
          >
            Vehicle pulling to one side, uneven tyre wear. Documented via garage diagnostics.
          </GuideCard>
        </GuideGrid>
      </GuideSection>

      <GuideSteps>
        <GuideStep number={1} title="Photograph Immediately">
          <p className="mb-2">
            As soon as it's safe to do so, take photos at the scene or as soon as you notice the damage. 
            The sooner you photograph, the stronger the link between incident and damage — and the smoother 
            the rest of your{' '}
            <Link to="/guides/claim-timeline" className={inlineLink}>
              claim timeline
            </Link>
            {' '}will be.
          </p>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>• Use good lighting — daylight is best</li>
            <li>• Include timestamps if your phone shows them</li>
            <li>• Don't clean the damage first (dirt/debris can show impact)</li>
          </ul>
        </GuideStep>

        <GuideStep number={2} title="Take Multiple Angles">
          <p className="mb-2">
            A single photo isn't enough. Take photos from different angles to fully document the damage:
          </p>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>• Wide shot showing the whole wheel/tyre area</li>
            <li>• Medium shot focusing on the damage</li>
            <li>• Close-up of the specific damage</li>
            <li>• Angle showing depth (for dents, cuts, or bulges)</li>
          </ul>
        </GuideStep>

        <GuideStep number={3} title="Include Context">
          <p className="mb-2">
            Show the damage in context so there's no doubt it's your vehicle:
          </p>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>• Include the registration plate in at least one shot</li>
            <li>• Show which wheel/corner of the vehicle</li>
            <li>• If possible, photograph near the pothole location</li>
          </ul>
        </GuideStep>

        <GuideStep number={4} title="Use a Reference Object">
          <p className="mb-2">
            For damage that's hard to gauge visually, include something for scale:
          </p>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>• A coin next to a tyre bulge or cut</li>
            <li>• A ruler if you have one</li>
            <li>• Your hand for rough scale</li>
          </ul>
        </GuideStep>
      </GuideSteps>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        }
        title="Linking Damage to the Incident"
      >
        <p className="mb-4">
          Councils may argue the damage was pre-existing or caused by something else. To establish the link:
        </p>
        <ul className="space-y-3 mb-4">
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Same wheel:</strong>{' '}
              <Link to="/guides/photographing-pothole-damage" className={inlineLink}>
                Photograph both the pothole
              </Link>
              {' '}and the damaged wheel — the wheel that hit the pothole</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Timeline:</strong> Note the date and time, ideally with photo metadata</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Fresh damage:</strong> Point out signs the damage is new (clean edges, no weathering)</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Expert assessment:</strong> Get a garage to confirm the damage is consistent with pothole impact when you{' '}
              <Link to="/guides/getting-repair-quotes" className={inlineLink}>
                obtain your repair quote
              </Link></span>
          </li>
        </ul>
        <GuideTip variant="warning" title="Important">
          If you only have damage photos without pothole photos, your claim is weaker. Always try to 
          photograph both.
        </GuideTip>
      </GuideSection>
    </GuideLayout>
  );
}
